/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import { Node } from 'tiptap';
import { replaceText } from 'tiptap-commands';
import SuggestionsPlugin from 'tiptap-extensions/src/plugins/Suggestions';
import { VBTooltip } from 'bootstrap-vue';

export default class Mention extends Node {
  static DATA_MENTION_ID = 'data-mention-id';

  static DEFAULT_OPTIONS = {
    matcher: {
      char: '@',
      allowSpaces: true,
      startOfLine: false,
    },
    mentionClass: 'mention',
    suggestionClass: 'mention-suggestion',
    customClasses: [],
    label: 'label',
    tooltip: 'value',
  };

  get name() {
    return 'mention';
  }

  get defaultOptions() {
    return Mention.DEFAULT_OPTIONS;
  }

  get schema() {
    return {
      attrs: {
        id: {},
        label: {},
        class: {
          default: this.options.mentionClass,
        },
      },
      group: 'inline',
      inline: true,
      selectable: false,
      atom: true,
      toDOM: node => [
        'span',
        {
          class: node.attrs.class,
          [Mention.DATA_MENTION_ID]: node.attrs.id,
        },
        `${this.options.matcher.char}${node.attrs.label}`,
      ],
      parseDOM: [
        {
          tag: `span[${Mention.DATA_MENTION_ID}]`,
          getAttrs: dom => {
            const id = dom.getAttribute(Mention.DATA_MENTION_ID);
            const label = dom.innerText.split(this.options.matcher.char).join('');
            return { id, label };
          },
        },
      ],
    };
  }

  get view() {
    // TODO: move to mention node component
    return {
      props: ['node', 'updateAttrs', 'view', 'options'],
      directives: {
        BTooltip: VBTooltip,
      },
      template: `<span v-b-tooltip.hover.top="tooltip" :class="classes" :${Mention.DATA_MENTION_ID}="node.attrs.id" 
      contenteditable="false">{{options.matcher.char}}{{label}}</span>`,
      computed: {
        classes() {
          let classes = [this.node.attrs.class];
          const mentionClasses = this.options.customClasses.filter(
            item => item.id.toString() === this.node.attrs.id.toString()
          );
          classes = classes.concat(mentionClasses.map(item => item.class));
          return classes;
        },
        tooltip() {
          const items = this.options.items();
          const currentItem = items.filter(item => item.id.toString() === this.node.attrs.id.toString());
          if (currentItem.length) return currentItem[0][this.options.tooltip];
        },
        label() {
          const items = this.options.items();
          const currentItem = items.filter(item => item.id.toString() === this.node.attrs.id.toString());
          if (currentItem.length) return currentItem[0][this.options.label] || this.node.attrs.id;
        },
      },
    };
  }

  commands({ schema }) {
    return attrs => replaceText(null, schema.nodes[this.name], attrs);
  }

  get plugins() {
    return [
      SuggestionsPlugin({
        command: ({ range, attrs, schema }) => replaceText(range, schema.nodes[this.name], attrs),
        appendText: ' ',
        matcher: this.options.matcher,
        items: this.options.items,
        onEnter: this.options.onEnter,
        onChange: this.options.onChange,
        onExit: this.options.onExit,
        onKeyDown: this.options.onKeyDown,
        onFilter: this.options.onFilter,
        suggestionClass: this.options.suggestionClass,
      }),
    ];
  }
}
